// Angular + external
import { Component, ViewChild, Inject, Optional, forwardRef, AfterViewInit } from "@angular/core";
// BFX
import { TieredMenu } from "@bfx/primeng/tieredmenu";
import { LoggerService } from "@bfx/logger";
const logger = LoggerService.for(__dirname);

import { NativeDesktopSupport } from "../bfx-native-desktop/native-desktop-support";
import { BfxAfterViewInitPromise } from "../bfx-common/helpers/bfx-after-view-init-promise";
import { BmarkSummaryContainerComponent } from "./bmark-summary-container.component";

export enum AmountViewType {
  MILLIONS,
  NOTIONAL,
}

const SETTINGS_MENU_MILLIONS = "Millions";
const SETTINGS_MENU_ICON_MILLIONS = "icon-toggle-amount-millions";
const SETTINGS_MENU_TOOLTIP_MILLIONS = "Show Benchmark Positions in millions.";

const SETTINGS_MENU_NOTIONAL = "Notional";
const SETTINGS_MENU_ICON_NOTIONAL = "icon-toggle-amount-notional";
const SETTINGS_MENU_TOOLTIP_NOTIONAL =
  "Show Benchmark Positions in notional terms, rather than millions.";

export abstract class BmarkSummaryContainerSettingsMenuHost {
  abstract hasAmountViewType(): boolean;
  abstract getAmountViewType(): AmountViewType;
  abstract setAmountViewType(amountViewType: AmountViewType);
  abstract showSettings(event: Event);
}

@Component({
  selector: "bmark-summary-settings-menu",
  templateUrl: "./html/bmark-summary-settings-menu.html",
  providers: [],
  standalone: false,
})
export class BmarkSummarySettingsMenuComponent implements AfterViewInit, BfxAfterViewInitPromise {
  afterViewInitResolve?: Function;

  afterViewInitReady: Promise<void> = new Promise<void>((res) => {
    this.afterViewInitResolve = res;
  });

  /***************************************/
  /* DATA */
  public menuItems: Array<any> = [];

  amountViewType: AmountViewType = AmountViewType.MILLIONS;

  @ViewChild(TieredMenu) menu: TieredMenu;

  /**********************************/
  /* CONSTRUCTOR + LIFE-CYCLE HOOKS*/

  constructor(
    @Optional()
    @Inject(forwardRef(() => BmarkSummaryContainerComponent))
    private _container: BmarkSummaryContainerSettingsMenuHost,
  ) {}

  ngAfterViewInit(): void {
    this.afterViewInitResolve();
    if (NativeDesktopSupport.isNativeDesktopContainer()) {
      // FIXME: NG8
      // NativeDesktopSupport.wrapDomHandler(this.menu.domHandler, true);
      let menu: any = this.menu;
      menu._appendOverlay = this.menu.appendOverlay;
      menu.appendOverlay = this.appendMenuOverlay.bind(this);
    }
  }

  private appendMenuOverlay(): void {
    let menu: any = this.menu;

    if (menu.appendTo === "body" && menu._document != null) {
      menu._document.body.appendChild(menu.container);
    } else {
      menu._appendOverlay();
    }
  }

  public toggle($event: any): void {
    this.afterViewInitReady.then(() => {
      if (NativeDesktopSupport.isNativeDesktopContainer()) {
        if (
          $event.target != null &&
          this.menu.container != null &&
          $event.target.ownerDocument !== this.menu.container.ownerDocument
        ) {
          let menu: any = this.menu;
          menu._document = $event.target.ownerDocument;

          if (!this.menu.container.offsetParent) {
            $event.target.ownerDocument.body.appendChild(this.menu.container);
            this.menu.unbindDocumentClickListener();
            this.menu.documentClickListener = this.menu.renderer.listen(
              $event.target.ownerDocument,
              "click",
              () => {
                if (this.menu == null) return;

                if (!this.menu.preventDocumentDefault) {
                  this.menu.hide();
                }
                this.menu.preventDocumentDefault = false;
              },
            );
          } else {
            this.menu.container.parentElement.removeChild(this.menu.container);
          }
        }
      }

      this.menuItems = this.constructMenu();
      this.menu.toggle($event);
    });
  }

  protected constructMenu(): Array<any> {
    var menuItems: Array<any> = [];
    let amountViewMenuDisplay: string = SETTINGS_MENU_MILLIONS;
    let amountViewIconClass: string = SETTINGS_MENU_ICON_MILLIONS;
    let amountViewTooltip: string = SETTINGS_MENU_TOOLTIP_MILLIONS;

    switch (this.amountViewType) {
      case AmountViewType.MILLIONS: {
        amountViewMenuDisplay = SETTINGS_MENU_NOTIONAL;
        amountViewIconClass = SETTINGS_MENU_ICON_NOTIONAL;
        amountViewTooltip = SETTINGS_MENU_TOOLTIP_NOTIONAL;

        break;
      }
      case AmountViewType.NOTIONAL: {
        amountViewMenuDisplay = SETTINGS_MENU_MILLIONS;
        amountViewIconClass = SETTINGS_MENU_ICON_MILLIONS;
        amountViewTooltip = SETTINGS_MENU_TOOLTIP_MILLIONS;
        break;
      }
    }

    // Settings
    menuItems.push({
      label: "Settings",
      icon: "bfx-icon-sm icon_ion_settings_filled",
      title: "Settings",
      command: (event) => this.settings(event),
    });

    if (this._container.hasAmountViewType()) {
      menuItems.push({
        label: `Show ${amountViewMenuDisplay} Amts`,
        icon: `bfx-icon-sm icon-base-delt ${amountViewIconClass}`,
        title: amountViewTooltip,
        command: (event) => this.toggleAmountViewType(),
      });
    }

    return menuItems;
  }

  public settings(event: Event): void {
    this._container.showSettings(event);
  }

  toggleAmountViewType() {
    switch (this.amountViewType) {
      case AmountViewType.MILLIONS: {
        this.amountViewType = AmountViewType.NOTIONAL;
        break;
      }
      case AmountViewType.NOTIONAL: {
        this.amountViewType = AmountViewType.MILLIONS;
        break;
      }
    }

    if (this._container) {
      this._container.setAmountViewType(this.amountViewType);
    }
  }
}
